<template>
  <VueFinalModal :esc-to-close="true" class="flex justify-center items-center"
    content-class="relative flex flex-col w-4/5 h-4/5 mx-auto bg-white border rounded-lg">
    <div class="flex flex-col h-full">
      <!-- Title Section (full width at top) -->
      <div class="p-4 border-b border-gray-200">
        <div class="text-xl font-semibold text-gray-900">
          {{ title }}
        </div>
      </div>

      <!-- Content Section -->
      <div class="flex flex-1 overflow-hidden">
        <!-- Files Section -->
        <div class="w-full flex-shrink-0 flex flex-col p-4">
          <div class="flex-1 overflow-y-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div v-for="(file, index) in files" :key="index" class="file-item">
                <!-- Image Files -->
                <div v-if="isImageFile(file)" class="image-container">
                  <img :src="file.url" :alt="file.filename || `File ${index + 1}`"
                    class="w-full h-48 object-cover rounded-lg shadow-md cursor-pointer hover:opacity-90 transition-opacity"
                    @click="openImageModal(file)" />
                  <div class="mt-2 text-sm text-gray-600 text-center">
                    {{ file.filename || `Image ${index + 1}` }}
                  </div>
                </div>

                <!-- Non-Image Files -->
                <div v-else class="file-container">
                  <div class="bg-gray-100 rounded-lg p-6 text-center">
                    <div class="text-4xl text-gray-400 mb-2">
                      📄
                    </div>
                    <div class="text-sm text-gray-600 mb-3">
                      {{ file.filename || `File ${index + 1}` }}
                    </div>
                    <a :href="file.url" :download="file.filename"
                      class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Close button -->
      <div class="absolute top-4 right-4">
        <button @click="closeModal()"
          class="bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup>
import { VueFinalModal } from 'vue-final-modal'
import { onMounted, ref, computed } from 'vue'

const props = defineProps({
  params: Object,
})

const emit = defineEmits(['close'])

let title = ref(props.params.data?.title || 'Files')
let files = ref([])

onMounted(() => {
  if (props.params.data?.files) {
    files.value = props.params.data.files
  }
})

function closeModal() {
  emit('close')
}

function isImageFile(file) {
  if (!file.content_type) return false
  return file.content_type.startsWith('image/')
}

function openImageModal(file) {
  // Use GLightbox to open the image in a modal
  if (window.GLightbox) {
    const lightbox = window.GLightbox({
      elements: [{
        href: file.url,
        type: 'image',
        title: file.filename || 'Image'
      }]
    })
    lightbox.open()
  } else {
    // Fallback to opening in new tab
    window.open(file.url, '_blank')
  }
}
</script>

<style>
.vm--modal {
  overflow: initial !important;
}

.file-item {
  transition: transform 0.2s ease-in-out;
}

.file-item:hover {
  transform: translateY(-2px);
}

.image-container img {
  transition: all 0.2s ease-in-out;
}

.file-container {
  transition: all 0.2s ease-in-out;
}

.file-container:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>